import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { collection, getDocs, query, where } from 'firebase/firestore';

import { useFeatureFlags } from '../../hooks';
import { useFirebaseStore } from '../../newStore/firebaseStore';
import { useUserDataStore } from '../../newStore/userDataStore';
import { useFormAnswerInfoQuery } from '../../queries';
import { Finding } from '../../types';
import { paths } from '../../utils/navigation';
import { AnswerViewExpiredLink } from './AnswerViewExpiredLink';
import FormAnswerForbiddenAccessState from '../NewWebForms/formAnswerForbiddenAccessState';
import { AxiosError } from 'axios';

interface AnswerViewContextType {
  findingsList: Finding[];
  isPublicView: boolean;
  taskFormId: string;
  formAnswerId: string;
  isEmbedded?: boolean;
  token?: string;
  pdfMode?: boolean;
  logoImageLoaded: boolean;
  setLogoImageLoaded: (loaded: boolean) => void;
}

const AnswerViewContext = createContext<AnswerViewContextType | null>(null);

interface AnswerViewProviderProps {
  children: ReactNode;
  formAnswerId?: string | number;
  taskFormId?: string | number;
  isEmbedded?: boolean;
  pdfMode?: boolean;
}

export const AnswerViewProvider = ({
  children,
  formAnswerId: propFormAnswerId,
  taskFormId: propTaskFormId,
  isEmbedded = false,
}: AnswerViewProviderProps) => {
  const { formId, answerId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const pdf_mode = searchParams.get('pdf_mode');
  const pdfMode = pdf_mode ? Boolean(pdf_mode) : false;
  const currentUser = useUserDataStore((state) => state.currentUser);

  const formAnswerId = propFormAnswerId?.toString() || answerId;
  const taskFormId = propTaskFormId?.toString() || formId;
  const isPublicView = !!token;
  const isPublicPath = location.pathname.startsWith('/public/');

  const { error: errorFormAnswer } = useFormAnswerInfoQuery(formAnswerId, token);
  const isForbiddenError = errorFormAnswer instanceof AxiosError && errorFormAnswer.response?.status === 403;
  const canAccessThisAnswer = !isForbiddenError;

  const { firestore, user, firebaseConfigured } = useFirebaseStore();
  const { getPremiumFlag } = useFeatureFlags();
  const findingsActivated = !!getPremiumFlag('Findings');

  const [findingsList, setFindingsList] = useState<Finding[]>([]);
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);

  if (isPublicPath && !isPublicView && !currentUser?.id) {
    return (
      <Navigate
        to={paths.auth.login}
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  useEffect(() => {
    async function getFindings() {
      try {
        const findingsQuery = query(
          collection(firestore, `datascope/${user.uid}/findings`),
          where('form_answer.id', '==', parseInt(formAnswerId)),
        );

        const findingsSnapshot = await getDocs(findingsQuery);
        const findingsList = findingsSnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          } as Finding;
        });
        setFindingsList(findingsList);
      } catch (error) {
        console.error('Error fetching findings:', error);
      }
    }
    if (!isPublicView && findingsActivated && firebaseConfigured) getFindings();
  }, [firebaseConfigured]);

  if (isPublicView && errorFormAnswer) return <AnswerViewExpiredLink />;

  if (currentUser?.id && !canAccessThisAnswer) {
    return <FormAnswerForbiddenAccessState />;
  }

  const answerViewValue = {
    findingsList,
    isPublicView,
    taskFormId,
    formAnswerId,
    isEmbedded,
    token,
    pdfMode,
    logoImageLoaded,
    setLogoImageLoaded,
  };
  return (
    <AnswerViewContext.Provider value={answerViewValue}>{children}</AnswerViewContext.Provider>
  );
};

export const useAnswerViewContext = () => useContext(AnswerViewContext);
